import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Progress } from '../ui/Progress';
import { Badge } from '../ui/Badge';
import { Calendar, Edit, Trash2, GripVertical, Trophy, Pause, Play } from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatDate, isOverdue } from '@/utils/formatters';
import { getPriorityStyle, getStatusStyle } from '@/utils/cardStyles';

export interface EntityCardProps {
  // Core data
  title: string;
  description?: string;
  status: string;
  statusLabel?: string;
  priority?: number;
  progress: number;
  deadline?: Date | string;

  // Display options
  showPriority?: boolean;
  showProgress?: boolean;
  isCompleted?: boolean;
  completedDate?: Date;

  // Active/Inactive state (for schedules)
  isActive?: boolean;

  // Visual customization
  className?: string;
  colorIndicator?: string;

  // Badges (flexible slot)
  badges?: React.ReactNode;

  // Additional content slots
  metadata?: React.ReactNode;
  footer?: React.ReactNode;

  // Drag and drop
  isDragging?: boolean;
  dragHandleProps?: any;

  // Actions
  onEdit?: () => void;
  onDelete?: () => void;
  onClick?: () => void;
  onToggleActive?: () => void;

  // Keyboard navigation
  tabIndex?: number;

  // Test ID
  testId?: string;
}

export function EntityCard({
  title,
  description,
  status,
  statusLabel,
  priority,
  progress,
  deadline,
  showPriority = false,
  showProgress = true,
  isCompleted = false,
  completedDate,
  isActive,
  className,
  colorIndicator,
  badges,
  metadata,
  footer,
  isDragging = false,
  dragHandleProps,
  onEdit,
  onDelete,
  onClick,
  onToggleActive,
  tabIndex = 0,
  testId = 'entity-card',
}: EntityCardProps) {
  const overdue = isOverdue(deadline, status);

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  const handleToggleActive = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleActive?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Only trigger onClick if Enter or Space is pressed on the card itself
    // (not when pressing on buttons/inputs inside)
    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      tabIndex={onClick ? tabIndex : undefined}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? `View ${title}` : undefined}
      className={cn(
        'entity-card',
        onClick && 'cursor-pointer hover:shadow-md transition-shadow',
        isDragging && 'entity-card-dragging',
        overdue && 'entity-card-overdue',
        isCompleted && 'entity-card-completed',
        className
      )}
    >
      {/* Color indicator strip */}
      {colorIndicator && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 rounded-l-md"
          style={{ backgroundColor: colorIndicator }}
        />
      )}

      <CardHeader className="entity-card-header">
        <div className="entity-card-header-row">
          <div className="entity-card-title-area">
            <div className="flex items-center gap-2">
              <CardTitle className={cn(
                'entity-card-title',
                isCompleted && 'line-through text-muted-foreground'
              )}>
                {title}
              </CardTitle>
              {isCompleted && (
                <Trophy className="h-5 w-5 text-green-600 flex-shrink-0" />
              )}
            </div>

            <div className="entity-card-badges">
              {showPriority && priority && (
                <Badge variant="outline" className={getPriorityStyle(priority)}>
                  #{priority}
                </Badge>
              )}
              <Badge className={getStatusStyle(status)}>
                {statusLabel || status}
              </Badge>
              {badges}
            </div>
          </div>

          <div className="entity-card-actions">
            {onToggleActive && (
              <Button
                data-testid={`${testId}-toggle-button`}
                variant="ghost"
                size="sm"
                onClick={handleToggleActive}
                className="entity-card-action-btn"
                title={isActive ? 'Pause' : 'Activate'}
              >
                {isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>
            )}
            {onEdit && (
              <Button
                data-testid={`${testId}-edit-button`}
                variant="ghost"
                size="sm"
                onClick={handleEdit}
                className="entity-card-action-btn"
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                data-testid={`${testId}-delete-button`}
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="entity-card-delete-btn"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            {dragHandleProps && (
              <div
                {...dragHandleProps}
                className="entity-card-drag-handle"
                role="button"
                aria-label={`Reorder ${title}. Press Space to activate, then use Up/Down arrows to move, Space to confirm or Escape to cancel.`}
                aria-roledescription="sortable"
              >
                <GripVertical className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="entity-card-content">
        {description && (
          <p className={cn(
            'entity-card-description',
            isCompleted && 'line-through'
          )}>
            {description}
          </p>
        )}

        {showProgress && (
          <div className="entity-card-progress">
            <div className="entity-card-progress-row">
              <span className="font-medium">Progress</span>
              <span className="text-muted-foreground">{progress}%</span>
            </div>
            <Progress
              value={progress}
              className={cn('h-2', isCompleted && 'bg-green-100')}
            />
          </div>
        )}

        {metadata}

        {deadline && (
          <div className={cn(
            'entity-card-deadline',
            overdue && 'entity-card-deadline-overdue',
            isCompleted && 'text-green-600'
          )}>
            <Calendar className="h-4 w-4" />
            <span>
              Deadline: {formatDate(deadline)}
              {overdue && ' (Overdue)'}
              {isCompleted && completedDate && ` (Completed ${formatDate(completedDate)})`}
            </span>
          </div>
        )}

        {footer}
      </CardContent>
    </Card>
  );
}
